import type { GlobalFieldAnchor, GlobalFieldPlacement, GlobalFieldRow } from './types';

/**
 * The option that means "every member, including ones added later".
 *
 * The backend carries this as a boolean beside a list (`is_all_lobs` + `lobs`), but a
 * multi-select holds one array — so the sentinel rides inside the array on the form and is
 * flattened back out on submit. `'all'` rather than a symbol because `CoverageDialog` and
 * `SharedCoverageDialog` already spell it that way, and a reader moving between the three
 * should not have to learn a second convention.
 */
export const ALL_SENTINEL = 'all';

export interface GlobalFieldFormValues {
  key: string;
  definition: Record<string, unknown>;
  placement: GlobalFieldPlacement;
  anchor: GlobalFieldAnchor;
  lobs: (string | number)[];
  sub_organization_ids: (string | number)[];
}

/** The two scope halves as the API takes them: a flag, and the list it falls back to. */
export interface ScopePayload {
  is_all_lobs: boolean;
  lobs: string[];
  is_all_sub_orgs: boolean;
  sub_organization_ids: number[];
}

/**
 * Flatten the form's sentinel-bearing arrays into the API's flag-plus-list pairs.
 *
 * The list is cleared when the flag is set rather than sent alongside it: the backend ignores
 * the list in that case, and sending a stale one would make the stored row disagree with what
 * the screen shows the next time the flag is cleared.
 */
export const toScopePayload = (values: {
  lobs: (string | number)[];
  sub_organization_ids: (string | number)[];
}): ScopePayload => {
  const isAllLobs = values.lobs.includes(ALL_SENTINEL);
  const isAllSubOrgs = values.sub_organization_ids.includes(ALL_SENTINEL);
  return {
    is_all_lobs: isAllLobs,
    lobs: isAllLobs ? [] : (values.lobs as string[]),
    is_all_sub_orgs: isAllSubOrgs,
    sub_organization_ids: isAllSubOrgs ? [] : (values.sub_organization_ids as number[]),
  };
};

/** The inverse, for seeding the form from a stored row. */
export const toScopeFormValues = (
  row: Pick<GlobalFieldRow, 'is_all_lobs' | 'lobs' | 'is_all_sub_orgs' | 'sub_organization_ids'> | null
): Pick<GlobalFieldFormValues, 'lobs' | 'sub_organization_ids'> => ({
  // A new field defaults to the whole organization, which is what "global" means until someone
  // narrows it.
  lobs: !row || row.is_all_lobs ? [ALL_SENTINEL] : row.lobs,
  sub_organization_ids: !row || row.is_all_sub_orgs ? [ALL_SENTINEL] : row.sub_organization_ids,
});
